import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { getRawOption, setRawOption } from '../../shared/helpers/cookie';
import { uidFromSessionToken } from '../../shared/helpers/session-token';

/** localStorage key for the signed session token (JWT) issued by the server. */
export const SESSION_TOKEN_KEY = 'andorSessionToken';

/**
 * Holds the signed session token (the server's `X-Andor-UUID` response
 * header) and persists it to localStorage. The token — not the raw
 * `andorUUID` — is the client's credential: the HTTP interceptor replays it
 * as `Authorization: Bearer` and the realtime channel dials with it, so
 * identity survives page reloads even where the cross-site cookie is blocked.
 *
 * Deliberately dependency-free (no HttpClient) so the HTTP interceptor can
 * inject it without a DI cycle.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  readonly sessionToken = signal<string>(getRawOption(SESSION_TOKEN_KEY));

  private readonly _identityChanged = new Subject<string>();
  /**
   * Emits the new uid when a token refresh changed the identity (uid claim
   * differs from the stored token's). The subscriber (AppService) re-keys the
   * WebSocket and re-syncs permissions.
   */
  readonly identityChanged$ = this._identityChanged.asObservable();

  /**
   * Adopt a (possibly re-minted) token from a server response header. The
   * server echoes the token on every response, so an expiry- or
   * key-rotation-driven re-mint is picked up on the next request — the
   * self-healing loop that keeps HTTP and WS identity converged.
   */
  updateFromHeader(token: string): void {
    const previous = this.sessionToken();
    if (token === previous) return;
    setRawOption(SESSION_TOKEN_KEY, token);
    this.sessionToken.set(token);
    const previousUid = previous ? uidFromSessionToken(previous) : null;
    const uid = uidFromSessionToken(token);
    if (uid && uid !== previousUid) {
      this._identityChanged.next(uid);
    }
  }
}
